import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type {
  BoolExprGroup,
  ConditionalData,
  DateTimeSetExpr,
  DayOfWeekEntry,
  SpecificDateEntry,
} from '@/proto/logiblocks/logiblocks';
import type {
  ConditionGroupReport,
  ConditionReport,
  ConditionalReport,
  DateTimeSetReport,
} from '@/proto/logiblocks/node_run_report';

dayjs.extend(utc);
dayjs.extend(timezone);

// TODO: Delete this file with `RunNodeAttemptsDrawer` and `ConditionalAttemptDetail`, its only
// readers, once the step results panel's Conditional detail shows every condition's report.

/** A date/time set condition from a Conditional's graph, with what the run reported for it. */
export interface DateTimeSetCondition {
  expr: DateTimeSetExpr;
  conditionReport: ConditionReport;
  dateTimeSetReport: DateTimeSetReport;
}

/**
 * Appends to `conditions` each date/time set condition in `group` and its nested groups that
 * `groupReport` reports a set for.
 */
function addGroupDateTimeSetConditions(
  group: BoolExprGroup,
  groupReport: ConditionGroupReport,
  conditions: DateTimeSetCondition[]
): void {
  // `groupReport.conditions[i]` reports on `group.expressions[i]`. A report of another length
  // can't be paired with the group's expressions.
  if (groupReport.conditions.length !== group.expressions.length) {
    console.error(
      `Conditional report has ${groupReport.conditions.length} conditions for a group of ` +
        `${group.expressions.length} expressions`
    );
    return;
  }
  for (let i = 0; i < group.expressions.length; i++) {
    const expression = group.expressions[i];
    const conditionReport = groupReport.conditions[i];
    if (expression.group !== undefined && conditionReport.group !== undefined) {
      addGroupDateTimeSetConditions(
        expression.group,
        conditionReport.group,
        conditions
      );
    }
    if (
      expression.date_time_set_expr !== undefined &&
      conditionReport.date_time_set !== undefined
    ) {
      conditions.push({
        expr: expression.date_time_set_expr,
        conditionReport,
        dateTimeSetReport: conditionReport.date_time_set,
      });
    }
  }
}

/**
 * Returns each date/time set condition in `conditional` that `report` reports a set for, in
 * graph order.
 */
export function getDateTimeSetConditions(
  report: ConditionalReport,
  conditional: ConditionalData
): DateTimeSetCondition[] {
  const conditions: DateTimeSetCondition[] = [];
  if (report.root === undefined || conditional.root === undefined) {
    console.error('Conditional report or its graph data has no root group');
    return conditions;
  }
  addGroupDateTimeSetConditions(conditional.root, report.root, conditions);
  return conditions;
}

/** One entry of the set a date/time set condition was checked against. */
export type RecordedEntry = SpecificDateEntry | DayOfWeekEntry;

/** Returns the set entries `dateTimeSetReport` recorded, which may be fewer than its `entry_count`. */
export function getRecordedEntries(
  dateTimeSetReport: DateTimeSetReport
): RecordedEntry[] {
  if (dateTimeSetReport.specific_dates !== undefined) {
    return dateTimeSetReport.specific_dates.dates;
  }
  if (dateTimeSetReport.days_of_week !== undefined) {
    return dateTimeSetReport.days_of_week.days;
  }
  return [];
}

/**
 * Returns the time `condition` checked against its set, in the set's zone: the variable's
 * date/time, or a range's start. Null if the set didn't resolve or the variable had no
 * date/time value.
 */
export function getEvaluatedAt(condition: DateTimeSetCondition): Dayjs | null {
  if (!condition.dateTimeSetReport.resolved) {
    return null;
  }
  const variableValue = condition.conditionReport.variable_value;
  const evaluatedValue =
    variableValue?.datetime_value ??
    variableValue?.date_time_range_value?.start;
  if (evaluatedValue === undefined) {
    return null;
  }
  return dayjs(evaluatedValue.unix_millis).tz(
    condition.dateTimeSetReport.iana_timezone
  );
}
